import React from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import _ from 'lodash';
import styled from 'styled-components';
import { withRouter } from 'react-router-dom';

import { changePagePagination } from 'common/actions/PageActions';
import { capfl } from 'common/utils/index';
import { withCssUnit } from 'common/components/styleguide/utils/index';
import { getPageDataSelector } from 'common/selectors/page';

const S = {};

S.ButtonsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0 0 10px 0;
  justify-content: center;
  @media (min-width: ${({ theme }) => withCssUnit(theme.breakpoints.md)}) {
    justify-content: ${({ justifyContent = 'flex-start' }) => justifyContent};
  }
`;

S.Button = styled.button`
  border-radius: ${({ theme }) => _.get(theme, 'pagination.basic.radius')};
  background: ${({ theme }) => _.get(theme, 'pagination.basic.background')};
  border: ${({ theme }) => _.get(theme, 'pagination.basic.border')};
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 48px;
  height: 48px;
  cursor: pointer;
  color: ${({ theme }) => _.get(theme, 'pagination.basic.color')};
  margin-right: 5px;
  outline: none;
  &:hover {
    color: ${({ theme }) => _.get(theme, 'pagination.hover.color')};
    border-radius: ${({ theme }) => _.get(theme, 'pagination.hover.radius')};
    background: ${({ theme }) => _.get(theme, 'pagination.hover.background')};
    border: ${({ theme }) => _.get(theme, 'pagination.hover.border')};
  }
  &:focus {
    outline: none;
  }
`;

S.ButtonActive = styled(S.Button)`
  && {
    color: ${({ theme }) => _.get(theme, 'pagination.active.color')};
    border-radius: ${({ theme }) => _.get(theme, 'pagination.active.radius')};
    background: ${({ theme }) => _.get(theme, 'pagination.active.background')};
    border: ${({ theme }) => _.get(theme, 'pagination.active.border')};
    &:hover {
      color: ${({ theme }) => _.get(theme, 'pagination.activeHover.color')};
      border-radius: ${({ theme }) =>
        _.get(theme, 'pagination.activeHover.radius')};
      background: ${({ theme }) =>
        _.get(theme, 'pagination.activeHover.background')};
      border: ${({ theme }) => _.get(theme, 'pagination.activeHover.border')};
    }
  }
`;

S.DotsButton = styled(S.Button)`
  && {
    cursor: default;
    border-color: transparent;
    color: #222;
    &:hover {
      cursor: default;
      color: #222;
      border-color: transparent;
    }
  }
`;

S.Main = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: ${({ theme }) => withCssUnit(_.get(theme, 'gutterWidth'))};
  margin-bottom: ${({ theme }) => withCssUnit(_.get(theme, 'gutterWidth'))};
  @media (min-width: ${({ theme }) => withCssUnit(theme.breakpoints.md)}) {
    margin: 0;
  }
`;

S.PaginationInfo = styled.div`
  color: ${({ theme }) => _.get(theme, 'colors.text')};
  font-size: 0.8em;
  text-align: center;
  margin: ${({ theme }) => withCssUnit(_.get(theme, 'gutterWidth'))} auto;
  @media (min-width: ${({ theme }) => withCssUnit(theme.breakpoints.md)}) {
    text-align: ${({ textAlign = 'left' }) => textAlign};
    justify-content: ${({ justifyContent = 'flex-start' }) => justifyContent};
  }
`;

const Pagination = ({ pagination, changePage, t, justifyContent = 'flex-start', textAlign = "left" }) => {
  if (_.isEmpty(pagination)) {
    return null;
  }

  const { totalCount, pageCount, currentPage, perPage } = pagination;

  const totalCountN = _.toNumber(totalCount);
  const perPageN = _.toNumber(perPage);
  const pageCountN = _.toNumber(pageCount);
  const firstPageN = 1;
  const currentPageN = _.toNumber(currentPage);

  const onChangePage = (pageNumber, e) => {
    e.preventDefault();
    changePage(pageNumber);
  };

  return (
    <section>
      <S.ButtonsWrapper justifyContent={justifyContent}>
        <S.Main>
          {currentPageN - 1 >= firstPageN ? (
            <S.Button onClick={(e) => onChangePage(currentPageN - 1, e)}>
              {capfl(t('previous'))}
            </S.Button>
          ) : null}

          {currentPageN !== firstPageN ? (
            <S.Button onClick={(e) => onChangePage(firstPageN, e)}>1</S.Button>
          ) : null}

          {currentPageN - 2 > firstPageN &&
          currentPageN - 2 !== firstPageN + 1 ? (
            <S.DotsButton key="pagination-dots-first">...</S.DotsButton>
          ) : null}

          {currentPageN - 2 == firstPageN + 1 ? (
            <S.Button onClick={(e) => onChangePage(currentPageN - 2, e)}>
              {currentPageN - 2}
            </S.Button>
          ) : null}

          {currentPageN - 1 > firstPageN ? (
            <S.Button onClick={(e) => onChangePage(currentPageN - 1, e)}>
              {currentPageN - 1}
            </S.Button>
          ) : null}

          <S.ButtonActive>{currentPageN}</S.ButtonActive>

          {currentPageN + 1 < pageCountN ? (
            <S.Button onClick={(e) => onChangePage(currentPageN + 1, e)}>
              {currentPageN + 1}
            </S.Button>
          ) : null}

          {currentPageN + 2 < pageCountN &&
          currentPageN + 2 !== pageCountN - 1 ? (
            <S.DotsButton key="pagination-dots-last">...</S.DotsButton>
          ) : null}

          {currentPageN + 2 == pageCountN - 1 ? (
            <S.Button onClick={(e) => onChangePage(currentPageN + 2, e)}>
              {currentPageN + 2}
            </S.Button>
          ) : null}

          {currentPageN !== pageCountN ? (
            <S.Button onClick={(e) => onChangePage(pageCountN, e)}>
              {pageCountN}
            </S.Button>
          ) : null}

          {currentPageN + 1 <= pageCountN ? (
            <S.Button onClick={(e) => onChangePage(currentPageN + 1, e)}>
              {capfl(t('next'))}
            </S.Button>
          ) : null}
        </S.Main>
      </S.ButtonsWrapper>
      <S.PaginationInfo justifyContent={justifyContent} textAlign={textAlign}>
        {capfl(t('showing'))}{' '}
        {currentPageN > 0 ? (currentPageN - 1) * perPageN : 0}-
        {currentPageN * perPageN} {t('from')} {totalCountN}{' '}
        {t('pagination results')}
      </S.PaginationInfo>
    </section>
  );
};

const makeMapStateToProps = () => {
  const getPageData = getPageDataSelector();
  return (state, props) => {
    return { pagination: _.get(getPageData(state, props), 'pagination') };
  };
};

const mapDispatchToProps = (dispatch, props) => {
  return {
    changePage: (page) => {
      const {
        history,
        callback = changePagePagination,
        afterClick = null,
        afterClickRef = null,
      } = props;
      if (afterClickRef) {
        dispatch(callback(page, history, afterClickRef));
      } else {
        dispatch(callback(page, history));
      }
      if (_.isFunction(afterClick)) {
        afterClick();
      }
    },
  };
};

const AppPaginationConnected = connect(
  makeMapStateToProps,
  mapDispatchToProps
)(Pagination);

export default React.memo(
  withTranslation()(withRouter(AppPaginationConnected))
);
